import { NgFor } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { AnimalGroupBarnMapping } from 'src/app/cycle/models/cycle.model';
import Utils from 'src/app/shared/utils';

import {
  CodelistDiagnosis,
  CodelistMedicine,
  CodelistQuantityUnit,
  EditAnimalGroupMode,
  MedicationDataRow,
} from '../animal-group.model';

export interface MedicationEditDialogInputData extends AbstractDialogData {
  medicationRow?: MedicationDataRow;
  mode: EditAnimalGroupMode;
  barnMappings: AnimalGroupBarnMapping[];
  codelistDiagnoses: CodelistDiagnosis[];
  codelistMedicines: CodelistMedicine[];
  codelistQuantityUnits: CodelistQuantityUnit[];
  countries: string[];
  locationCountry: string;
}

export interface MedicationEditDialogResultData {
  medicationRow: MedicationDataRow;
}

@Component({
  selector: 'app-medication-edit.component',
  templateUrl: 'medication-edit.component.html',
  styleUrl: 'medication-edit.component.scss',
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatTooltip,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class MedicationEditComponent
  extends AbstractDialog<MedicationEditDialogResultData>
  implements OnInit
{
  data = inject<MedicationEditDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<MedicationEditComponent, AbstractDialogResultData>>(MatDialogRef);
  private readonly localeService = inject(LocaleService);

  id: number;
  codelistMedicinesByCountry: CodelistMedicine[] = [];
  selectedCountry: string = null;

  formGroup = new FormGroup({
    barn: new FormControl<AnimalGroupBarnMapping>(null, [Validators.required]),
    startDate: new FormControl<Date>(null, [Validators.required]),
    endDate: new FormControl<Date>(null, []),
    country: new FormControl<string>(null, [Validators.required]),
    medicine: new FormControl<CodelistMedicine>(null, [Validators.required]),
    diagnosis: new FormControl<CodelistDiagnosis>(null),
    quantity: new FormControl<number>(null),
    unit: new FormControl<CodelistQuantityUnit>(null),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.localeService.translate(this.data.mode);
  }

  ngOnInit() {
    if (this.data.mode === EditAnimalGroupMode.Edit && this.data.medicationRow !== null) {
      const selectedBarn = this.data.barnMappings.find(
        (b) => b.barnId === this.data.medicationRow?.barn?.barnId,
      );
      const selectedMedicine = this.data.codelistMedicines.find(
        (v) => v.plukonId === this.data.medicationRow.medicine.plukonId,
      );
      const selectedQuantityUnit = this.data.codelistQuantityUnits.find(
        (unit) => unit.codeUnit === this.data.medicationRow.appliedQuantity?.unitCode,
      );
      const selectedDiagnosis = this.data.codelistDiagnoses.find(
        (diagnosis) => diagnosis.code === this.data.medicationRow.diagnoseCode,
      );
      this.selectedCountry = this.data.medicationRow.country;

      this.formGroup.controls.barn.setValue(selectedBarn);
      this.formGroup.controls.startDate.setValue(new Date(this.data.medicationRow.startDate));
      this.formGroup.controls.endDate.setValue(new Date(this.data.medicationRow.endDate));
      this.formGroup.controls.country.setValue(this.selectedCountry);
      this.formGroup.controls.medicine.setValue(selectedMedicine);
      this.formGroup.controls.diagnosis.setValue(selectedDiagnosis);
      this.formGroup.controls.quantity.setValue(
        this.data.medicationRow.appliedQuantity?.measuredValue ?? null,
      );
      this.formGroup.controls.unit.setValue(selectedQuantityUnit);
    } else {
      this.selectedCountry = this.data.locationCountry;
    }
  }

  updateCodelistMedicinesByCountry(country: string) {
    this.codelistMedicinesByCountry = this.data.codelistMedicines.filter(
      (v) => v.country === country,
    );
  }

  countryChanged(country: string) {
    this.updateCodelistMedicinesByCountry(country);
  }

  save() {
    this.saveMedication();
  }

  private saveMedication() {
    if (this.formGroup.valid) {
      super.confirm({
        medicationRow: {
          id: this.data.medicationRow?.id,
          barn: this.formGroup.controls.barn.value,
          startDate: Utils.parseDateTimeFromFormValue(this.formGroup.controls.startDate.value),
          endDate: Utils.parseDateTimeFromFormValue(this.formGroup.controls.endDate.value),
          country: this.formGroup.controls.country.value,
          medicine: this.formGroup.controls.medicine.value,
          diagnoseCode: this.formGroup.controls.diagnosis.value?.code,
          diagnoseName: this.formGroup.controls.diagnosis.value?.name,
          appliedQuantity: {
            measuredValue: this.formGroup.controls.quantity.value,
            unitCode: this.formGroup.controls.unit.value?.codeUnit,
          },
        },
      });
    } else {
      this.formGroup.markAllAsTouched();
    }
  }
}
